import React, { useState } from "react";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Plus, Loader2 } from "lucide-react";
import moment from "moment";

const EQUIPMENT_TYPES = { machinery: "Maquinària", installation: "Instal·lació", safety_device: "Equip seguretat", electrical: "Electrical", other: "Altres" };
const MAINTENANCE_TYPES = { preventive: "Preventiu", corrective: "Correctiu", inspection: "Inspecció", calibration: "Calibratge", repair: "Reparació" };
const STATUS_COLORS = { pending: "bg-amber-100 text-amber-700", in_progress: "bg-blue-100 text-blue-700", completed: "bg-emerald-100 text-emerald-700", needs_repair: "bg-red-100 text-red-700" };

export default function EquipmentMaintenanceList({ venue_id, maintenance, onChanged, isAdmin }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ equipment_name: "", equipment_type: "machinery", maintenance_type: "preventive", maintenance_date: new Date().toISOString().split("T")[0], description: "", responsible_name: "", status: "pending" });
  const [saving, setSaving] = useState(false);

  const openForm = (item = null) => {
    setEditing(item);
    setForm(item ? { ...form, ...item } : { equipment_name: "", equipment_type: "machinery", maintenance_type: "preventive", maintenance_date: new Date().toISOString().split("T")[0], description: "", responsible_name: "", status: "pending", venue_id });
    setShowForm(true);
  };

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSave = async () => {
    if (!form.equipment_name || !form.maintenance_date) return;
    setSaving(true);
    if (editing?.id) {
      await hosys.entities.EquipmentMaintenance.update(editing.id, { ...form, venue_id });
    } else {
      await hosys.entities.EquipmentMaintenance.create({ ...form, venue_id });
    }
    setSaving(false);
    setShowForm(false);
    onChanged();
  };

  const handleDelete = async (id) => {
    await hosys.entities.EquipmentMaintenance.delete(id);
    onChanged();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Manteniment d'Equips</h3>
        {isAdmin && (
          <Button onClick={() => openForm()} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 text-sm" size="sm">
            <Plus className="w-4 h-4" /> Registrar manteniment
          </Button>
        )}
      </div>

      {maintenance.length === 0 ? (
        <Card className="p-8 text-center border-0 shadow-sm"><p className="text-gray-500">Cap manteniment registrat</p></Card>
      ) : (
        <div className="space-y-2">
          {maintenance.map(m => (
            <Card key={m.id} className="p-4 border-0 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="font-semibold text-gray-900">{m.equipment_name}</h4>
                    <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">{EQUIPMENT_TYPES[m.equipment_type]}</Badge>
                    <Badge className="bg-gray-100 text-gray-700 border-0 text-[10px]">{MAINTENANCE_TYPES[m.maintenance_type]}</Badge>
                    <Badge className={`border-0 text-[10px] ${STATUS_COLORS[m.status]}`}>{m.status}</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{m.description}</p>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-500">
                    <div>Data: {moment(m.maintenance_date).format("DD/MM/YYYY")}</div>
                    {m.next_maintenance_date && <div>Próxim: {moment(m.next_maintenance_date).format("DD/MM/YYYY")}</div>}
                    {m.responsible_name && <div>Responsable: {m.responsible_name}</div>}
                    {m.cost && <div>Cost: {m.cost}€</div>}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-[#7B2D3F]" onClick={() => openForm(m)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-red-600" onClick={() => handleDelete(m.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={o => !o && setShowForm(false)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar manteniment" : "Registrar manteniment"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Equip *</Label><Input value={form.equipment_name} onChange={e => set("equipment_name", e.target.value)} placeholder="Nom de l'equip" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipus equip</Label><Select value={form.equipment_type} onValueChange={v => set("equipment_type", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(EQUIPMENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Tipus manteniment</Label><Select value={form.maintenance_type} onValueChange={v => set("maintenance_type", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(MAINTENANCE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data *</Label><Input type="date" value={form.maintenance_date} onChange={e => set("maintenance_date", e.target.value)} /></div>
              <div><Label>Próxim manteniment</Label><Input type="date" value={form.next_maintenance_date || ""} onChange={e => set("next_maintenance_date", e.target.value)} /></div>
            </div>
            <div><Label>Descripció</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} placeholder="Detalls del manteniment..." /></div>
            <div><Label>Resultats/Observacions</Label><Textarea value={form.findings || ""} onChange={e => set("findings", e.target.value)} rows={2} placeholder="Resultats de la inspecció..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Responsable</Label><Input value={form.responsible_name || ""} onChange={e => set("responsible_name", e.target.value)} placeholder="Nom" /></div>
              <div><Label>Tècnic</Label><Input value={form.technician_name || ""} onChange={e => set("technician_name", e.target.value)} placeholder="Tècnic" /></div>
            </div>
            <div><Label>Empresa externa (si aplica)</Label><Input value={form.external_company || ""} onChange={e => set("external_company", e.target.value)} placeholder="Empresa" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cost (€)</Label><Input type="number" value={form.cost || ""} onChange={e => set("cost", parseFloat(e.target.value) || 0)} min="0" step="0.01" /></div>
              <div><Label>Estat</Label><Select value={form.status} onValueChange={v => set("status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(STATUS_COLORS).map(([k, v]) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel·lar</Button>
            <Button onClick={handleSave} disabled={!form.equipment_name || !form.maintenance_date || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editing ? "Actualitzar" : "Registrar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}